/* ============================================================
   ALAYA JUNIOR — Editorial hero (Session 2)
   Minimal vanilla slideshow: fade between slides, dots, autoplay (pauses on
   hover/focus + when the tab is hidden), touch swipe, and reduced-motion
   awareness. One custom element instance per section.
   ============================================================ */
class EditorialHero extends HTMLElement {
  constructor() {
    super();
    this.slides = Array.from(this.querySelectorAll('.editorial-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.editorial-hero__dot'));
    this.index = 0;
    this.timer = null;
    this.speed = parseInt(this.dataset.speed, 10) || 5000;
    this.autoplay =
      this.dataset.autoplay === 'true' &&
      this.slides.length > 1 &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    if (this.slides.length <= 1) return;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.goTo(parseInt(dot.dataset.index, 10));
        this.restart();
      });
    });

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    let startX = 0;
    this.addEventListener(
      'touchstart',
      (e) => {
        startX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );
    this.addEventListener(
      'touchend',
      (e) => {
        const delta = e.changedTouches[0].screenX - startX;
        if (Math.abs(delta) < 40) return;
        this.goTo(delta < 0 ? this.index + 1 : this.index - 1);
        this.restart();
      },
      { passive: true }
    );

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else this.start();
    });

    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  goTo(next) {
    const count = this.slides.length;
    this.index = ((next % count) + count) % count;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('aria-hidden', !active);
    });
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-selected', 'true');
      else dot.removeAttribute('aria-selected');
    });
  }

  start() {
    if (!this.autoplay || this.timer) return;
    this.timer = setInterval(() => this.goTo(this.index + 1), this.speed);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('editorial-hero')) {
  customElements.define('editorial-hero', EditorialHero);
}
